import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

interface ToastProps {
  title: string;
  message: string;
  type: 'success' | 'error' | 'info';
  duration?: number;
  onClose: () => void;
  url?: string;
}

const Toast: React.FC<ToastProps> = ({ 
  title, 
  message, 
  type = 'info', 
  duration = 5000, 
  onClose,
  url
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Esperar a que termine la animación
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClick = () => {
    if (url) {
      window.location.href = url;
    }
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'error':
        return <ExclamationCircleIcon className="h-6 w-6 text-red-500" />;
      case 'info':
      default:
        return <InformationCircleIcon className="h-6 w-6 text-domired-500" />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900 dark:bg-opacity-20';
      case 'error':
        return 'bg-red-50 dark:bg-red-900 dark:bg-opacity-20';
      case 'info':
      default:
        return 'bg-domired-50 dark:bg-domired-900 dark:bg-opacity-20';
    }
  };

  return (
    <div 
      className={`fixed top-4 right-4 max-w-sm w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto border ${
        type === 'success' ? 'border-green-200 dark:border-green-700' : 
        type === 'error' ? 'border-red-200 dark:border-red-700' : 
        'border-domired-200 dark:border-domired-700'
      } transform transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
      } z-50`}
      role="alert"
      aria-live="assertive"
      onClick={handleClick}
      style={{ cursor: url ? 'pointer' : 'default' }}
    >
      <div className={`rounded-t-lg px-4 py-2 ${getBackgroundColor()}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">{message}</p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="bg-transparent rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                setIsVisible(false);
                setTimeout(onClose, 300);
              }}
            >
              <span className="sr-only">Cerrar</span>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Toast;
